// @flow

import * as React from 'react';

import {AlignBox} from 'sail/Align';
import Button, {ButtonGroup} from 'sail/Button';
import Card from 'sail/Card';
import FormLayout, {FormBlock, FormField, FormRow} from 'sail/FormLayout';
import Group from 'sail/Group';
import {Heading, BodyTextProps} from 'sail/Text';
import {InfoTooltip} from 'sail/Tooltip';
import {cancelCircle} from 'sail/Icon/Assets';
import Notice from 'sail/Notice';

import {preloadedQuery, SearchableSelect, useSearch} from 'sail/Search';
import Select, {SelectOption} from 'sail/Select';

import type {Query} from 'sail/Search/types';
import type {SearchableSelectResultProps} from 'sail/Search';
import type {ConnectRequiredInfoSelection} from './ConnectRequiredInfo';

const {useState} = React;

type ConnectRequiredInfoSelectorProps = {|
  capabilityTypes: Array<string>,
  countryMap: {[string]: string},
  countryTosMap: {[string]: Array<string>},
  entityTypeStructureMap: {
    [country: string]: {
      [biz_type: string]: {[structure: string]: string | null},
    },
  },
  curSelection: ConnectRequiredInfoSelection,
  setSelection: ($Shape<ConnectRequiredInfoSelection>) => void,
|};

export const defaultSelection: ConnectRequiredInfoSelection = {
  capabilityTypeSet: new Set(['card_payments', 'transfers']),
  country: 'US',
  tosType: 'full',
  entityType: 'company',
  businessStructure: null,
};

export const loadFragment = (
  fragmentStr: ?string,
): ?ConnectRequiredInfoSelection => {
  if (!fragmentStr) {
    return null;
  }

  const [selection, capabilityStr] = fragmentStr.split('--', 2);
  if (!selection || !capabilityStr) {
    return null;
  }

  const [country, tosType, entityAndStructure] = selection.split('-', 3);
  if (!country || !tosType || !entityAndStructure) {
    return null;
  }

  if (tosType !== 'full' && tosType !== 'recipient') {
    return null;
  }

  const [entityType, businessStructure] = entityAndStructure.split('.');
  if (!entityType) {
    return null;
  }

  const capabilities = decodeURIComponent(capabilityStr).split('|');
  if (capabilities.length === 0) {
    return null;
  }

  return {
    capabilityTypeSet: new Set(capabilities),
    country,
    tosType,
    entityType,
    businessStructure: businessStructure || null,
  };
};

export const ConnectRequiredInfoSelector = (
  props: ConnectRequiredInfoSelectorProps,
) => {
  const {
    capabilityTypes,
    countryMap,
    countryTosMap,
    entityTypeStructureMap,
    curSelection,
    setSelection,
  } = props;

  const capabilityEntries = capabilityTypes.map((capability) => ({capability}));

  const setFragment = (selection: ConnectRequiredInfoSelection): void => {
    const fragment = `${selection.country}-${selection.tosType}-${
      selection.entityType
    }${
      selection.businessStructure ? `.${selection.businessStructure}` : ''
    }--${[...selection.capabilityTypeSet].sort().join('|')}`;
    history.pushState(null, '', `#${fragment}`);
  };

  // Partial state update helper
  const updateSelection = (selection: Object) => {
    const newSelection = {...curSelection, ...selection};

    if (!countryMap[newSelection.country]) {
      newSelection.country = defaultSelection.country;
    }

    if (!countryTosMap[newSelection.country].includes(newSelection.tosType)) {
      newSelection.tosType = countryTosMap[newSelection.country][0];
    }

    if (
      entityTypeStructureMap[newSelection.country][newSelection.entityType] ===
      undefined
    ) {
      newSelection.entityType = Object.keys(
        entityTypeStructureMap[newSelection.country],
      )[0];
    }

    if (
      newSelection.businessStructure &&
      !(
        newSelection.businessStructure in
        entityTypeStructureMap[newSelection.country][newSelection.entityType]
      )
    ) {
      newSelection.businessStructure = null;
    }

    if (newSelection.tosType === 'recipient') {
      newSelection.capabilityTypeSet = new Set(['transfers']);
    }

    setSelection(newSelection);
    setFragment(newSelection);
  };

  // capability membership set helpers
  const addCapability = (capability: ?string) => {
    if (!capability) {
      return;
    }

    const newCapabilitySet = new Set(curSelection.capabilityTypeSet);

    if (
      curSelection.tosType === 'recipient' &&
      capability === 'card_payments'
    ) {
      return;
    }

    newCapabilitySet.add(capability);
    if (capability === 'card_payments') {
      newCapabilitySet.add('transfers');
    }
    updateSelection({
      capabilityTypeSet: newCapabilitySet,
    });
  };

  const removeCapability = (capability) => {
    const newCapabilitySet = new Set(curSelection.capabilityTypeSet);
    newCapabilitySet.delete(capability);
    if (capability === 'transfers') {
      newCapabilitySet.delete('card_payments');
    }
    updateSelection({
      capabilityTypeSet: newCapabilitySet,
    });
  };

  const countrySelector = (
    <Select
      id="countrySelector"
      name="country"
      value={curSelection.country}
      onChange={(val) => updateSelection({country: val})}
    >
      {Object.keys(countryMap).map((code) => (
        <SelectOption label={countryMap[code]} value={code} key={code} />
      ))}
    </Select>
  );

  const tosSelector = (
    <Select
      id="tosTypeSelector"
      nae="tosType"
      value={curSelection.tosType}
      onChange={(val) => updateSelection({tosType: val})}
    >
      {countryTosMap[curSelection.country].map((tosItem) => (
        <SelectOption label={tosItem} value={tosItem} key={tosItem} />
      ))}
    </Select>
  );

  const entitySelector = (
    <Select
      id="entityType"
      name="entityType"
      value={curSelection.entityType}
      onChange={(val) => updateSelection({entityType: val})}
    >
      {Object.keys(entityTypeStructureMap[curSelection.country]).map(
        (entity) => (
          <SelectOption label={entity} value={entity} key={entity} />
        ),
      )}
    </Select>
  );

  const shouldShowBusinessStructureSelector =
    Object.keys(
      entityTypeStructureMap[curSelection.country][curSelection.entityType],
    ).length > 0;

  const businessStructureSelector = (
    <Select
      id="businessStructure"
      name="businessStructure"
      value={curSelection.businessStructure}
      onChange={(val) => updateSelection({businessStructure: val})}
    >
      <SelectOption label="" value="" />
      {Object.entries(
        entityTypeStructureMap[curSelection.country][curSelection.entityType],
      ).map(([structure, label]: [string, any]) => (
        <SelectOption
          label={label ? `${label} (${structure})` : structure}
          value={structure}
          key={structure}
        />
      ))}
    </Select>
  );

  type CapabilityEntry = {capability: string};
  const getSearchableText = (capability: CapabilityEntry) =>
    capability.capability;
  const getButtonLabel = (entry: CapabilityEntry) => entry.capability;
  const queryCapabilities: Query<CapabilityEntry> = preloadedQuery(
    getSearchableText,
    capabilityEntries,
  );
  const renderItem = (
    renderProps: SearchableSelectResultProps<CapabilityEntry>,
  ) => `${renderProps.entry.capability}`;

  const CapabilitySelector = (selectorProps) => {
    const searchProps = useSearch(queryCapabilities);
    const [selectedEntry, setSelectedEntry] = useState(null);

    return (
      <SearchableSelect
        {...searchProps}
        autoflip
        chooseLabel="Add a capability..."
        getButtonLabel={getButtonLabel}
        getSearchableText={getSearchableText}
        inputPlaceholder="Search available capabilities..."
        onChange={(entry: ?CapabilityEntry) => {
          setSelectedEntry(entry);
          selectorProps.handleAdd(entry?.capability);
        }}
        renderItem={renderItem}
        value={selectedEntry}
      />
    );
  };

  type CapabilitiesListProps = {|
    capabilityTypeSet: Set<string>,
    removeCapability: (string) => void,
  |};

  const CapabilitiesList = (capabilitiesListProps: CapabilitiesListProps) => {
    const {capabilityTypeSet} = capabilitiesListProps;
    const hasCardPayments = capabilityTypeSet.has('card_payments');

    const getTransfersNote = () => {
      const transferText = (
        <>
          The <code>transfers</code> capability is required when the{' '}
          <code>card_payments</code> capability is requested.
        </>
      );

      return hasCardPayments ? <Notice description={transferText} /> : null;
    };

    const getRecipientNote = () => {
      const recipientText = (
        <>
          The <code>card_payments</code> capability is not available for
          accounts with a <code>recipient</code> service agreement.
        </>
      );

      return curSelection.tosType === 'recipient' ? (
        <Notice description={recipientText} />
      ) : null;
    };

    const shouldDisable = (capability) => {
      return (
        capabilityTypeSet.size < 2 ||
        (capability === 'transfers' && hasCardPayments)
      );
    };

    return (
      <Group spacing={12}>
        <ButtonGroup wrap>
          {[...capabilitiesListProps.capabilityTypeSet].map((capability) => (
            <Button
              label={<code>{capability}</code>}
              key={capability}
              size="small"
              icon={cancelCircle}
              iconPosition="right"
              disabled={shouldDisable(capability)}
              onClick={(e) =>
                capabilitiesListProps.removeCapability(capability)
              }
            />
          ))}
        </ButtonGroup>
        {getTransfersNote()}
        {getRecipientNote()}
      </Group>
    );
  };

  // Fragment Processing
  const [preSelected, setPreselected] = useState(false);

  React.useEffect(() => {
    if (!preSelected) {
      setPreselected(true);

      const preSelection = loadFragment(window.location.hash.substr(1));
      if (preSelection) {
        updateSelection(preSelection);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [curSelection]);

  return (
    <Group margin={{top: 20}} spacing={20}>
      <form>
        <Card shadow="medium">
          <FormLayout background="offset">
            <FormBlock
              rowDividers
              title={<Heading>Viewing requirements for</Heading>}
            >
              <FormRow name="countryRow" label="Country:">
                <FormField>{countrySelector}</FormField>
              </FormRow>
              <FormRow name="tosRow" label="Service Agreement:">
                <FormField>{tosSelector}</FormField>
              </FormRow>
              <FormRow name="entityRow" label="Business Type:">
                <AlignBox reference={BodyTextProps}>
                  <Group direction="horizontal" spacing={16}>
                    <FormField>{entitySelector}</FormField>
                    <Group
                      id={'businessStructureGroup'}
                      style={{
                        display: shouldShowBusinessStructureSelector
                          ? 'block'
                          : 'none',
                      }}
                      direction="horizontal"
                      spacing={8}
                    >
                      <FormField>{businessStructureSelector}</FormField>
                      <InfoTooltip content="You are able to further classify some business types by identifing their legal business structure. Providing this information might reveal you have fewer onboarding requirements to collect." />
                    </Group>
                  </Group>
                </AlignBox>
              </FormRow>
              <FormRow name="capabilityRow" label="Capabilities:">
                <FormField>
                  <CapabilitySelector handleAdd={addCapability} />
                </FormField>
                <FormField>
                  <CapabilitiesList
                    capabilityTypeSet={curSelection.capabilityTypeSet}
                    removeCapability={removeCapability}
                  />
                </FormField>
              </FormRow>
            </FormBlock>
          </FormLayout>
        </Card>
      </form>
      <Heading>
        Minimum verification requirements for:{' '}
        {countryMap[curSelection.country]}
      </Heading>
    </Group>
  );
};
